/**
 * Debit note: a certified sale invoice (FV, or EV for an export original)
 * that charges a client an additional sum on an invoice already issued.
 * The DGI annex has no debit-note type, so the document is a sale like any
 * other; its designation, the reference to the original and the reason
 * travel on two §2.27 comment lines.
 *
 * Each line prints under the label the tenant's « Lignes de commentaires »
 * table gives its code (§2.27): « Note de débit » and « Motif ».
 *
 * The two lines are stored in `Invoice.commentLines` with a `debitNote`
 * marker beside `type` and `content`; the marker is what lists the debit
 * notes of an invoice. The MCF and the printed page read `type` and
 * `content` only.
 */
/** Reasons offered for a debit note; each also requires a free-text detail. */
export const DEBIT_NOTE_REASONS = {
    SOUS_FACTURATION: {
        code: "SOUS_FACTURATION",
        label: "Correction de sous-facturation",
        hint: "Prix erroné, frais de livraison oubliés",
    },
    FRAIS_ANNEXES: {
        code: "FRAIS_ANNEXES",
        label: "Frais annexes",
        hint: "Pénalités de retard, frais bancaires, intérêts sur une créance",
    },
    AJUSTEMENT_COMMANDE: {
        code: "AJUSTEMENT_COMMANDE",
        label: "Ajustement de commande",
        hint: "Articles ajoutés après la facture d'origine",
    },
    AUTRE: {
        code: "AUTRE",
        label: "Autre",
        hint: "Motif précisé dans le détail",
    },
};
export const DEBIT_NOTE_REASON_CODES = Object.keys(DEBIT_NOTE_REASONS);
export function isDebitNoteReasonCode(value) {
    return typeof value === "string" && Object.prototype.hasOwnProperty.call(DEBIT_NOTE_REASONS, value);
}
/** Invoice types a debit note complements; the debit note takes the same type. */
export const DEBIT_NOTE_ORIGINAL_TYPES = ["FV", "EV"];
export function isDebitNoteOriginalType(type) {
    return DEBIT_NOTE_ORIGINAL_TYPES.includes(type);
}
/** Longest detail accepted, in characters; the 36h frame bounds the line further. */
export const DEBIT_NOTE_DETAIL_MAX_LENGTH = 150;
/**
 * Comment-line codes a debit note may take: the lines the annex reserves
 * (C to H). A and B carry DGI labels of their own (exemption reference,
 * legal basis).
 */
export const DEBIT_NOTE_COMMENT_LINE_CODES = ["C", "D", "E", "F", "G", "H"];
/** Labels of the two comment-line rows a debit note prints under, per role. */
export const DEBIT_NOTE_COMMENT_LINE_LABELS = {
    REFERENCE: "Note de débit",
    MOTIF: "Motif",
};
/** dd/mm/yyyy of an instant, in UTC (the legal time of Burkina Faso). */
function frenchDate(date) {
    const day = String(date.getUTCDate()).padStart(2, "0");
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    return `${day}/${month}/${date.getUTCFullYear()}`;
}
/**
 * Content of the reference line, printed under the label « Note de débit »:
 * the number of the original, and its date when known.
 */
export function debitNoteReferenceText(originalNumber, originalDate) {
    const date = originalDate === null ? "" : ` du ${frenchDate(originalDate)}`;
    return `complément de la facture n° ${originalNumber}${date}`;
}
/**
 * Content of the reason line, printed under the label « Motif »: the reason
 * label then the detail; the detail alone for AUTRE.
 */
export function debitNoteMotifText(reason, detail) {
    const trimmed = detail.trim();
    return reason === "AUTRE" ? trimmed : `${DEBIT_NOTE_REASONS[reason].label} — ${trimmed}`;
}
function markerOf(entry) {
    if (typeof entry !== "object" || entry === null)
        return null;
    const marker = entry.debitNote;
    return typeof marker === "object" && marker !== null ? marker : null;
}
/** Whether `value` is a debit-note marker of the exact stored shape. */
export function isDebitNoteMarker(value) {
    if (typeof value !== "object" || value === null || Array.isArray(value))
        return false;
    const marker = value;
    const keys = Object.keys(marker).sort().join(",");
    const id = marker["originalInvoiceId"];
    if (typeof id !== "string" || id === "")
        return false;
    if (marker["role"] === "REFERENCE") {
        return keys === "originalInvoiceId,originalNumber,role" && typeof marker["originalNumber"] === "string";
    }
    if (marker["role"] === "MOTIF") {
        return (keys === "detail,originalInvoiceId,reason,role" &&
            isDebitNoteReasonCode(marker["reason"]) &&
            typeof marker["detail"] === "string");
    }
    return false;
}
/** Whether a stored comment line is one of the two lines of a debit note. */
export function isDebitNoteCommentLine(entry) {
    const marker = markerOf(entry);
    return marker !== null && (marker["role"] === "REFERENCE" || marker["role"] === "MOTIF");
}
/**
 * The debit note an invoice declares: null unless it is an FV or EV whose
 * stored comment lines carry both markers of one debit note.
 */
export function debitNoteOf(invoice) {
    return isDebitNoteOriginalType(invoice.type) ? readDebitNote(invoice.commentLines) : null;
}
/**
 * The debit note an invoice's stored comment lines declare, or null when
 * they declare none or only part of one.
 */
export function readDebitNote(commentLines) {
    if (!Array.isArray(commentLines))
        return null;
    let reference = null;
    let motif = null;
    for (const entry of commentLines) {
        const marker = markerOf(entry);
        if (marker?.["role"] === "REFERENCE")
            reference = marker;
        if (marker?.["role"] === "MOTIF")
            motif = marker;
    }
    if (reference === null || motif === null)
        return null;
    const originalInvoiceId = reference["originalInvoiceId"];
    const originalNumber = reference["originalNumber"];
    const reason = motif["reason"];
    const detail = motif["detail"];
    if (typeof originalInvoiceId !== "string" ||
        typeof originalNumber !== "string" ||
        motif["originalInvoiceId"] !== originalInvoiceId ||
        !isDebitNoteReasonCode(reason) ||
        typeof detail !== "string") {
        return null;
    }
    return {
        originalInvoiceId,
        originalNumber,
        reason,
        reasonLabel: DEBIT_NOTE_REASONS[reason].label,
        detail,
    };
}
function isDebitNoteCommentLineCode(code) {
    return DEBIT_NOTE_COMMENT_LINE_CODES.includes(code);
}
/**
 * The row the debit-note line of `role` prints under: the first row of C–H
 * whose label is exactly DEBIT_NOTE_COMMENT_LINE_LABELS of that role, or
 * undefined while none carries it.
 */
export function debitNoteCommentLineRow(rows, role) {
    const label = DEBIT_NOTE_COMMENT_LINE_LABELS[role];
    return rows.find((row) => isDebitNoteCommentLineCode(row.code) && row.label === label);
}
/**
 * Codes of the rows the debit-note lines print under, reference first.
 * Only the debit-note service writes a line on them: a line without the
 * `debitNote` marker is refused there and not offered at entry.
 */
export function debitNoteCommentLineRowCodes(rows) {
    return ["REFERENCE", "MOTIF"]
        .map((role) => debitNoteCommentLineRow(rows, role)?.code)
        .filter((code) => code !== undefined);
}
/**
 * The codes the two debit-note lines take among C–H (§2.27). For each role,
 * its row (`debitNoteCommentLineRow`); else the first row still reserved
 * that the invoice's other lines (`taken`) leave free, which the label then
 * completes. Null when a role finds neither.
 */
export function debitNoteCommentLineCodes(rows, taken) {
    const reserved = rows.filter((row) => isDebitNoteCommentLineCode(row.code) && row.attributes.isReserved && !taken.has(row.code));
    const pick = (role) => {
        const label = DEBIT_NOTE_COMMENT_LINE_LABELS[role];
        const labelled = debitNoteCommentLineRow(rows, role);
        if (labelled)
            return { code: labelled.code, label, labelled: true };
        const free = reserved.shift();
        return free ? { code: free.code, label, labelled: false } : null;
    };
    const reference = pick("REFERENCE");
    const motif = pick("MOTIF");
    return reference && motif ? { reference, motif } : null;
}
//# sourceMappingURL=debit-note.js.map